import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, patch, post } from "common/helpers/HTTP";
import type {
  DetailedWorkspace,
  RentedSubscription,
  Subscription,
  SubscriptionSettings,
  SubscriptionType,
  WorkspaceAccount,
} from "common/types";

async function createSubscriptionRequest(
  workspaceId: string,
  planId: string,
  count: number,
  type: SubscriptionType,
  paymentSourceId: string,
): Promise<{
  workspace: DetailedWorkspace;
}> {
  const { workspace } = await post(`workspaces/${workspaceId}/subscriptions`, {
    item_price_id: planId,
    item_count: count,
    type,
    payment_source_id: paymentSourceId,
  });
  return { workspace };
}

async function addCouponRequest(
  subscriptionId: string,
  coupon: string,
): Promise<SubscriptionSettings> {
  return (
    await patch(`subscriptions/${subscriptionId}/coupon`, {
      coupon,
    })
  ).subscription;
}

async function removeChangeRequest(
  workspaceId: string,
  subscriptionId: string,
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await del(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/changes`,
  );

  return { workspace: detailedWorkspace, accounts };
}

type CancelSubscriptionResponse = {
  status: string;
  subscription: Subscription;
};

function cancelSubscriptionRequest(
  workspaceId: string,
  subscriptionId: string,
  reason: string,
): Promise<CancelSubscriptionResponse> {
  return post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/cancel`,
    { reason },
  );
}

export default function useWorkspaceSubscriptionMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const {
    updateCachedWorkspaceDetails,
    updateCachedWorkspaceList,
    updateCachedWorkspaceAccounts,
  } = useWorkspaceCacheUpdate();

  const {
    mutateAsync: createWorkspaceSubscription,
    isPending: isCreatingWorkspaceSubscription,
    error: createWorkspaceSubscriptionError,
  } = useMutation({
    mutationFn: ({
      planId,
      count,
      type,
      paymentSourceId,
    }: {
      planId: string;
      count: number;
      type: SubscriptionType;
      paymentSourceId: string;
    }) =>
      createSubscriptionRequest(
        workspaceId,
        planId,
        count,
        type,
        paymentSourceId,
      ),
    onSuccess: ({ workspace }, { type }) => {
      updateCachedWorkspaceDetails(workspaceId, () => {
        return workspace;
      });
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        if (type === "user") {
          draftWorkspace.subscription_status = "active";
          draftWorkspace.license_count = workspace.license_count;
        } else {
          draftWorkspace.rental_subscription_status = "active";
        }
      });
    },
  });

  const { mutateAsync: addCoupon, isPending: isAddingCoupon } = useMutation({
    mutationFn: ({
      subscriptionId,
      coupon,
    }: {
      subscriptionId: string;
      coupon: string;
    }) => addCouponRequest(subscriptionId, coupon),
    onSuccess: (subscriptionSettings) => {
      toast.success("Coupon added");

      queryClient.setQueryData(
        ["subscriptionSettings", subscriptionSettings.id],
        subscriptionSettings,
      );
    },
    onError: () => toast.error("Failed to add coupon"),
  });

  const { mutateAsync: removeChange, isPending: isRemovingChange } =
    useMutation({
      mutationFn: ({
        subscription,
      }: {
        subscription: Subscription | RentedSubscription;
      }) => removeChangeRequest(workspaceId, subscription.id),
      onSuccess: ({ workspace, accounts }, { subscription }) => {
        updateCachedWorkspaceDetails(workspaceId, () => {
          return workspace;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          if (workspace.subscription) {
            draftWorkspace.subscription_status = workspace.subscription.status;
          }
          if (workspace.rental_subscription) {
            draftWorkspace.rental_subscription_status =
              workspace.rental_subscription.status;
          }
        });
        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });

        queryClient.invalidateQueries({
          queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
            subscription.id,
          ),
        });
      },
      onError: () => toast.error("Failed to remove scheduled change"),
    });

  const {
    mutateAsync: cancelSubscription,
    isPending: isCancelingSubscription,
  } = useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason: string;
    }) => cancelSubscriptionRequest(workspaceId, subscriptionId, reason),
    onSuccess: ({ subscription }) => {
      updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
        draftWorkspace.subscription = subscription;
        draftWorkspace.subscription_status = subscription.status;
      });
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        draftWorkspace.subscription_status = subscription.status;
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          subscription.id,
        ),
      });
    },
  });

  return {
    createWorkspaceSubscription,
    isCreatingWorkspaceSubscription,
    createWorkspaceSubscriptionError,

    removeChange,
    isRemovingChange,

    cancelSubscription,
    isCancelingSubscription,

    addCoupon,
    isAddingCoupon,
  };
}
